import { Alert, Box, LinearProgress, Skeleton } from '@mui/material';
import { BodyText } from '@/components/BodyText';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetLimitStatus } from './assetLimitStatus';

const progressColorByStatus = {
  normal: 'primary',
  warning: 'warning',
  reached: 'error',
} as const;

const AssetLimitReachedMessage = () => (
  <Alert severity='error' data-testid='asset-limit-message'>
    Asset limit reached. New uploads will fail.
  </Alert>
);

const AssetLimitWarningMessage = ({ uploadsRemaining }: { uploadsRemaining: number }) => (
  <Alert severity='warning' data-testid='asset-limit-message'>
    Approaching your asset limit: {uploadsRemaining.toLocaleString()} {uploadsRemaining === 1 ? 'upload' : 'uploads'}{' '}
    remaining.
  </Alert>
);

const LimitUsage = ({
  assetsUsed,
  assetLimit,
  warningThresholdPercent,
}: {
  assetsUsed: number;
  assetLimit: number;
  warningThresholdPercent?: number;
}) => {
  const usagePercentage = Math.min(100, (assetsUsed / assetLimit) * 100);
  const status = getAssetLimitStatus({ assetsUsed, assetLimit, warningThresholdPercent });

  return (
    <Box display='flex' flexDirection='column' gap='12px'>
      <Box>
        <LinearProgress variant='determinate' value={usagePercentage} color={progressColorByStatus[status]} />
        <BodyText color='text.secondary' fontSize='0.75rem'>
          {`${usagePercentage.toFixed(0)}% of asset limit`}
        </BodyText>
      </Box>
      {status === 'warning' && <AssetLimitWarningMessage uploadsRemaining={assetLimit - assetsUsed} />}
      {status === 'reached' && <AssetLimitReachedMessage />}
    </Box>
  );
};

export const UsageSection = ({ assetsUsed, isLoading }: { assetsUsed: number; isLoading: boolean }) => {
  const { tenant } = useTenants();
  const assetLimit = tenant?.assetLimit;
  const usageText = assetLimit
    ? `${assetsUsed.toLocaleString()} / ${assetLimit.toLocaleString()}`
    : assetsUsed.toLocaleString();
  const usageLabel = assetLimit ? 'assets used' : 'assets uploaded';

  if (isLoading) {
    return <Skeleton variant='rounded' height={48} />;
  }

  return (
    <Box display='flex' flexDirection='column' gap='12px'>
      <Box>
        <BodyText fontSize='1.5rem' fontWeight={600} data-testid='usage-count'>
          {usageText}
        </BodyText>
        <BodyText color='text.secondary' display='block'>
          {usageLabel}
        </BodyText>
      </Box>
      {!!assetLimit && (
        <LimitUsage
          assetsUsed={assetsUsed}
          assetLimit={assetLimit}
          warningThresholdPercent={tenant?.assetLimitWarningThresholdPercent}
        />
      )}
    </Box>
  );
};
